import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Loader2, Plus, Check } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["Strip", "Bottle", "Tube", "Vial", "Pack", "Piece", "Box", "Jar"];

export function MedTypeManager({ open, onOpenChange, types, onSaved }) {
  const [edits, setEdits] = useState({});
  const [units, setUnits] = useState({});
  const [busy, setBusy] = useState("");
  const [newName, setNewName] = useState("");
  const [newUnit, setNewUnit] = useState("Pack");

  useEffect(() => {
    if (open) {
      setEdits({});
      setUnits({});
      setNewName("");
      setNewUnit("Pack");
    }
  }, [open]);

  const rename = async (t) => {
    const label = (edits[t.key] ?? t.label).trim();
    setBusy(t.key);
    try {
      await api.put(`/med-types/${t.key}`, { label, unit_label: t.is_custom ? (units[t.key] ?? t.unit_label) : undefined });
      toast.success(`"${label}" save ho gaya — sab jagah update ho jayega`);
      setEdits((p) => { const n = { ...p }; delete n[t.key]; return n; });
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy("");
    }
  };

  const addType = async () => {
    setBusy("new");
    try {
      await api.post("/med-types", { label: newName.trim(), unit_label: newUnit });
      toast.success("Type add ho gaya — POS aur forms mein turant dikhega");
      setNewName("");
      setNewUnit("Pack");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white max-h-[85vh] overflow-y-auto" data-testid="med-type-manager-dialog">
        <DialogHeader><DialogTitle className="font-display">Manage Medicine Types</DialogTitle><DialogDescription className="sr-only">Add a new medicine type or rename existing types</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500">Naam change karo ya naya type add karo — save karte hi POS, medicine forms aur har jagah live ho jayega.</div>
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg" data-testid="type-list">
            {types.map((t) => {
              const val = edits[t.key] ?? t.label;
              const unitVal = units[t.key] ?? t.unit_label;
              const changed = val.trim() !== t.label || (t.is_custom && unitVal !== t.unit_label);
              return (
                <div key={t.key} className="flex items-center gap-2 p-2" data-testid={`type-row-${t.key}`}>
                  <Input value={val} onChange={(e) => setEdits((p) => ({ ...p, [t.key]: e.target.value }))} className="h-9 flex-1" data-testid={`type-label-${t.key}`} />
                  {t.is_custom ? (
                    <Select value={unitVal} onValueChange={(v) => setUnits((p) => ({ ...p, [t.key]: v }))}>
                      <SelectTrigger className="h-9 w-24 bg-white shrink-0" data-testid={`type-unit-${t.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className="shrink-0 text-[10px] text-slate-500 font-normal">{t.unit_label}</Badge>
                  )}
                  <Button size="sm" variant="outline" disabled={!changed || busy === t.key || !val.trim()} onClick={() => rename(t)} data-testid={`type-save-${t.key}`} className="shrink-0 px-2">
                    {busy === t.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-3 space-y-2">
            <Label className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Naya Type Add Karein</Label>
            <div className="flex items-center gap-2">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Toothpick, Whey Protein" className="h-9 flex-1 bg-white" data-testid="new-type-name" />
              <Select value={newUnit} onValueChange={setNewUnit}>
                <SelectTrigger className="h-9 w-24 bg-white shrink-0" data-testid="new-type-unit"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" onClick={addType} disabled={!newName.trim() || busy === "new"} data-testid="add-type-btn" className="bg-emerald-900 hover:bg-emerald-800 shrink-0 px-2">
                {busy === "new" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <p className="text-[11px] text-slate-400">Jaise Whey Protein 5kg jar ya 500g pack — alag size ke liye alag medicine entry banayein, unit same rahega.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="type-manager-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
